import type {Component} from 'grapesjs';
import type {ColorPaletteInfo} from '@Components/auto-branding/auto-branding.types';
import {ColorPaletteType} from '@Components/auto-branding/auto-branding.types';
import type {AutoBrandingIntroVisualProps} from '@Components/auto-branding/components/auto-branding-intro-visual';
import {Type} from '@Components/button';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {getGA4StyleTypeFromColorPalette, storeAppliedPaletteInHtml} from '@Libraries/auto-branding-email-maker';
import {getAssetUrl} from '@Utils/s3.util';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export const PALETTE_CUSTOM_STYLE = 'color-palette';
export const PALETTE_APPLIED_EVENT = 'custom:palette:applied';

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  const AUTO_BRANDING_ELEMENT_ID = 'auto-branding-element-id';
  const AUTO_BRANDING_PANEL_ID = 'auto-branding-panel-id';
  const INTRO_GRAPHICS_SOURCE = getAssetUrl('auto-branding-first-time-visual-background.png');

  let currentlyAppliedColorPalette: ColorPaletteInfo | null = null;
  let initialHtml = '';

  styleManager.addType(window.PMW.emails.styles.ID_AUTO_BRANDING, {
    create(data) {
      const {change} = data;

      const autoBrandingContainer = document.createElement('div');
      autoBrandingContainer.id = AUTO_BRANDING_ELEMENT_ID;

      const colorPalettePanelContainer = document.createElement('div');
      colorPalettePanelContainer.id = AUTO_BRANDING_PANEL_ID;

      window.addEventListener(PALETTE_APPLIED_EVENT, (event): void => {
        change({event});
      });

      storeInitialHtml();
      storeInitialColorPalette();
      renderAutoBrandingComponent(autoBrandingContainer);

      return autoBrandingContainer;
    },

    emit() {
      const componentContianer = document.querySelector(`#${AUTO_BRANDING_ELEMENT_ID}`);

      if (!componentContianer) {
        return;
      }

      renderAutoBrandingComponent(componentContianer);
    },
  });

  const storeInitialHtml = (): void => {
    initialHtml = editor.runCommand(window.PMW.emails.commands.GET_EDITABLE_HTML) as string;
  };

  const storeInitialColorPalette = (): void => {
    const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
    const colorPaletteValue = editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_STYLE, {component: mainWrapper, property: PALETTE_CUSTOM_STYLE}) as string;

    currentlyAppliedColorPalette = colorPaletteValue ? (JSON.parse(colorPaletteValue) as ColorPaletteInfo) : getDefaultColorPalette();
  };

  const getDefaultColorPalette = (): ColorPaletteInfo | null => {
    const allColorsOnEmail = editor.runCommand(window.PMW.emails.commands.GET_ALL_COLORS_HEXES_IN_COMPONENT, {component: editor.getWrapper()}) as string[];
    const allNonLightColors = allColorsOnEmail.filter((color) => {
      return !/f[a-f]{3}/.exec(color);
    });

    if (allNonLightColors.length === 0) {
      return null;
    }

    const allColors = [...new Set(allNonLightColors)].sort().slice(0, 5);

    return {
      colors: allColors.map((color) => {
        return `#${color}`;
      }),
    };
  };

  const renderAutoBrandingComponent = (componentContainer: Element): void => {
    window.PMW.renderAutoBranding(componentContainer, {
      firstTimeVisualProps: getFirstTimeVisualProps(),
      onPaletteAdd: openPanelHandler,
      onPaletteSelect: openPanelHandler,
      onPaletteEdit: openPanelHandler,
      appliedPalette: currentlyAppliedColorPalette,
    });
  };

  const brandKitUpdateHandler = (appliedColorPalette: ColorPaletteInfo, suggestedPalettes: ColorPaletteInfo[]): void => {
    const componentContainer = document.querySelector(`#${AUTO_BRANDING_PANEL_ID}`) ?? document.createElement('div');

    window.PMW.renderBrandKitPaletteEditor(componentContainer, {
      text: window.i18next.t('pmwjs_brand_kit_palette_auto_generation'),
      buttonCtaText: window.i18next.t('pmwjs_update_brand_kit'),
      buttonType: Type.PRIMARY,
      brandKitPalette: currentlyAppliedColorPalette,
      appliedPalette: appliedColorPalette,
      suggestedPalettes,
      onPaletteSelect: (selectedPalette: ColorPaletteInfo) => {
        colorPaletteChangeHandler(selectedPalette, false);
      },
      onBrandKitUpdate: openPanelHandler,
      colorPaletteAppliedEvent: PALETTE_APPLIED_EVENT,
    });

    const title = currentlyAppliedColorPalette?.name ?? window.i18next.t('pmwjs_styles');
    editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {title, content: componentContainer, onBack: openPanelHandler});

    currentlyAppliedColorPalette = appliedColorPalette;
    storeAppliedPaletteInHtml(editor, appliedColorPalette);
  };

  const colorPaletteChangeHandler = (selectedColorPalette: ColorPaletteInfo, updateColorPalette = true): void => {
    editor.runCommand(window.PMW.emails.commands.APPLY_AUTO_BRANDING_STYLE, {
      colorPalette: selectedColorPalette,
      html: initialHtml,
      onSuccess: storeAppliedPaletteInHtml,
      updateColorPalette: updateColorPalette && shouldUpdateColorPaletteOnApplication(selectedColorPalette),
      onUpdatedColorPalette: brandKitUpdateHandler,
    });

    currentlyAppliedColorPalette = selectedColorPalette;
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_APPLIED, {[GA4EventParamName.TYPE]: getGA4StyleTypeFromColorPalette(selectedColorPalette)});
  };

  const openPanelHandler = (): void => {
    const componentContainer = document.querySelector(`#${AUTO_BRANDING_PANEL_ID}`) ?? document.createElement('div');

    window.PMW.renderColorPalettePanel(componentContainer, {
      onColorPaletteSelect: colorPaletteChangeHandler,
      appliedColorPalette: currentlyAppliedColorPalette,
      colorPaletteAppliedEvent: PALETTE_APPLIED_EVENT,
    });

    editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {title: window.i18next.t('pmwjs_styles'), content: componentContainer});
    editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_SCROLL_LISTENER, {toggleOn: false});
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_VISITED);
  };

  const getFirstTimeVisualProps = (): AutoBrandingIntroVisualProps => {
    return {
      heading: window.i18next.t('pmwjs_styles'),
      description: window.i18next.t('pmwjs_email_maker_styles_description'),
      buttonCtaText: window.i18next.t('pmwjs_try_it_now'),
      graphicSource: INTRO_GRAPHICS_SOURCE,
      buttonCta: openPanelHandler,
    };
  };

  const shouldUpdateColorPaletteOnApplication = (selectedColorPalette: ColorPaletteInfo): boolean => {
    return selectedColorPalette.type === ColorPaletteType.BrandKit;
  };
};
